import { z } from "zod"

import {
  branchIdSchema,
  correlationIdSchema,
  entityIdSchema,
  legalEntityIdSchema,
} from "./identifiers.js"

const base = z.object({
  requestVersion: z.literal(1),
  correlationId: correlationIdSchema,
  idempotencyKey: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/),
  expectedVersion: z.number().int().nonnegative(),
}).strict()

export const financialPostingLineV1Schema = z.object({
  lineId: entityIdSchema,
  account: z.string().trim().min(1).max(140),
  branchId: branchIdSchema,
  costCenter: z.string().trim().min(1).max(140),
  debitMinor: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
  creditMinor: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
  partyType: z.enum(["Customer", "Supplier"]).optional(),
  party: z.string().trim().min(1).max(140).optional(),
}).strict().superRefine((value, context) => {
  if ((value.debitMinor === 0) === (value.creditMinor === 0)) {
    context.addIssue({
      code: "custom",
      message: "Each line must contain exactly one positive side.",
    })
  }
  if ((value.partyType === undefined) !== (value.party === undefined)) {
    context.addIssue({
      code: "custom",
      message: "Party type and party must be supplied together.",
    })
  }
})

export const financialPostingCreateV1Schema = base.extend({
  postingId: entityIdSchema,
  legalEntityId: legalEntityIdSchema,
  configurationId: entityIdSchema,
  configurationVersion: z.number().int().positive(),
  currency: z.string().regex(/^[A-Z]{3}$/),
  postingDate: z.iso.date(),
  originalEventAt: z.iso.datetime({ offset: true }),
  purpose: z.enum([
    "receivable",
    "payable",
    "payment",
    "tax",
    "allocation",
    "provider_summary",
    "opening_balance",
    "adjustment",
  ]),
  sourceType: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/),
  sourceId: entityIdSchema,
  sourceVersion: z.number().int().positive(),
  remarks: z.string().trim().min(1).max(1_000),
  lines: z.array(financialPostingLineV1Schema).min(2).max(100),
}).strict().superRefine((value, context) => {
  const debit = value.lines.reduce((sum, line) => sum + line.debitMinor, 0)
  const credit = value.lines.reduce((sum, line) => sum + line.creditMinor, 0)
  if (!Number.isSafeInteger(debit) || debit !== credit || debit === 0) {
    context.addIssue({ code: "custom", message: "Posting must balance." })
  }
})

export const financialPostingReverseV1Schema = base.extend({
  postingId: entityIdSchema,
  reversalId: entityIdSchema,
  legalEntityId: legalEntityIdSchema,
  postingDate: z.iso.date(),
  reason: z.string().trim().min(1).max(1_000),
}).strict()

export const financialPostingResultV1Schema = z.object({
  entityType: z.literal("financial_posting"),
  entityId: entityIdSchema,
  state: z.enum(["posted", "reversed"]),
  version: z.number().int().positive(),
  legalEntityId: legalEntityIdSchema,
  currency: z.string().regex(/^[A-Z]{3}$/),
  totalMinor: z.number().int().positive(),
  erpnextDocumentType: z.literal("Journal Entry"),
  erpnextDocumentId: z.string().trim().min(1).max(140),
  reversalId: entityIdSchema.nullable(),
}).strict()

export type FinancialPostingCreateV1 = z.infer<
  typeof financialPostingCreateV1Schema
>
export type FinancialPostingReverseV1 = z.infer<
  typeof financialPostingReverseV1Schema
>
